import { useCallback, useEffect, useRef, useState } from 'react';

export type ServerEvent =
  | { type: 'ready' }
  | { type: 'state'; status: string; message?: string }
  | {
      type: 'transcript';
      speaker: string | null;
      text: string;
      lang: string | null;
      isFinal: boolean;
      ts: number;
    }
  | {
      type: 'suggestion';
      kind:
        | 'question'
        | 'answer'
        | 'warning'
        | 'translation'
        | 'term'
        | 'observation'
        | 'recall';
      title: string | null;
      body: string;
      trigger: string | null;
      ts: number;
    }
  | { type: 'error'; message: string }
  | { type: 'goAway'; timeLeftMs: number }
  | { type: 'end'; reason?: string };

export type LiveStatus =
  | 'idle'
  | 'connecting'
  | 'ready'
  | 'streaming'
  | 'paused'
  | 'ended'
  | 'error';

export function useLiveSession(meetingId: string) {
  const wsRef = useRef<WebSocket | null>(null);
  const meetingIdRef = useRef(meetingId);
  meetingIdRef.current = meetingId;
  const [status, setStatus] = useState<LiveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const listenersRef = useRef<Set<(e: ServerEvent) => void>>(new Set());

  const onEvent = useCallback((cb: (e: ServerEvent) => void) => {
    listenersRef.current.add(cb);
    return () => {
      listenersRef.current.delete(cb);
    };
  }, []);

  const dispatch = useCallback((e: ServerEvent) => {
    listenersRef.current.forEach((cb) => {
      try {
        cb(e);
      } catch {}
    });
  }, []);

  const connect = useCallback(() => {
    const id = meetingIdRef.current;
    if (!id) return;
    const existing = wsRef.current;
    if (existing && (existing.readyState === WebSocket.CONNECTING || existing.readyState === WebSocket.OPEN)) return;

    setStatus('connecting');
    setError(null);
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws?meeting_id=${encodeURIComponent(id)}`);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    ws.onmessage = (msg) => {
      if (typeof msg.data !== 'string') return;
      let ev: ServerEvent;
      try {
        ev = JSON.parse(msg.data) as ServerEvent;
      } catch {
        return;
      }
      if (ev.type === 'ready') setStatus('ready');
      else if (ev.type === 'error') {
        setError(ev.message);
        setStatus('error');
      } else if (ev.type === 'end') {
        setStatus((s) => (s === 'error' ? 'error' : 'ended'));
      }
      dispatch(ev);
    };
    ws.onclose = () => {
      setStatus((s) => (s === 'error' ? 'error' : s === 'idle' ? 'idle' : 'ended'));
    };
    ws.onerror = () => {
      setError('WebSocket error');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendBinary = useCallback((buf: ArrayBuffer) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(buf);
  }, []);

  const sendJson = useCallback((data: unknown) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(data));
  }, []);

  const stop = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'control', action: 'end' })); } catch {}
    }
  }, []);

  const close = useCallback(() => {
    try { wsRef.current?.close(); } catch {}
  }, []);

  const setStreaming = useCallback(() => setStatus('streaming'), []);

  // Cleanup on unmount only.
  useEffect(() => {
    return () => {
      try { wsRef.current?.close(); } catch {}
    };
  }, []);

  return { status, error, connect, sendBinary, sendJson, stop, close, onEvent, setStreaming };
}
